import React, { useContext, useEffect, useState } from "react";
import { Button, Card, CardBody, Col, Form, InputGroup, Row } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { Controller, Resolver, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { UserService } from "./service";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import { useTranslation } from "react-i18next";
import FormDropzone from "../../shared/dropzone";
import { EnumsContext } from "../../context/EnumsContext";
import Select from "react-select";
import SignaturePad from "../../shared/components/SignaturePad";
import { IPhoto } from "../../shared/interfaces/shared/IBase";
import { useUnsavedChanges } from "../../shared/hooks/useUnsavedChanges";

const SUPPORTED_FORMATS = ["image/jpg", "image/jpeg", "image/png"];

// schema will be created inside the component so we can make password required only on create

interface UserState {
  firstName: string;
  lastName: string;
  email: string;
  mobile?: string;
  password?: string;
  isActive: boolean;
  image?: File[];
  signature?: File;
  roles: string[]; // always an array, never undefined
  signatureImage?: IPhoto;
}

const UserForm: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const isEdit = Boolean(id);
  // build validation schema depending on create/edit mode
  const schema = yup.object().shape({
    firstName: yup.string().required("validation.required"),
    lastName: yup.string().notRequired(),
    about: yup.string().notRequired(),
    email: yup.string().email("Invalid email").required("validation.required"),
    mobile: yup.string().notRequired(),
    // password required only on create
    password: isEdit ? yup.string().notRequired() : yup.string().required("validation.required"),
    signature: yup
      .mixed()
      .nullable()
      .notRequired()
      .test("fileType", "Unsupported file format", (value: any) => {
        if (!value) return true;
        return SUPPORTED_FORMATS.includes(value.type);
      }),
    roles: yup.array().of(yup.string().notRequired()).notRequired(),
    isActive: yup.boolean().notRequired(),
  });
  const [image, setImage] = useState<File[]>([]);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const [existingFiles, setExistingFiles] = useState<any[]>([]);
  const { enums, loading } = useContext(EnumsContext);
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isDirty },
  } = useForm<UserState>({
    resolver: yupResolver(schema) as unknown as Resolver<UserState>,
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      mobile: "",
      password: "",
      isActive: false,
      image: [],
      signature: undefined,
      roles: [],
    },
  });

  const { guardedNavigate, unsavedChangesModal } = useUnsavedChanges(isDirty);
  const { signatureImage } = useWatch({
    control,
  });
  useEffect(() => {
    if (isEdit) {
      const fetchUser = async () => {
        try {
          const response = await UserService.getSingle(+id!);
          reset(response);
          setExistingFiles([response.profileImage]);
        } catch (error) {
          ErrorToast(error);
        }
      };
      fetchUser();
    }
  }, [isEdit, id]);

  // Handle file uploads from dropzone
  const handleFileUpload = (files: File[]) => {
    setImage(files);
    setValue("image", files, { shouldDirty: true });
  };

  const onSubmit = async (data: UserState) => {
    try {
      const formData = new FormData();
      const { image, signature, ...rest } = data;
      formData.append("body", JSON.stringify(rest));
      if (image && image.length > 0) {
        formData.append("image", image[0]);
      }
      signature && formData.append("signature", signature);
      if (isEdit) {
        await UserService.update(formData, +id!);
      } else {
        await UserService.create(formData);
      }
      SuccessToast(t(`user.${isEdit ? "successUpdate" : "successCreate"}`));
      navigate("/users");
      reset();
    } catch (error) {
      ErrorToast(error);
    }
  };

  const roleOptions =
    enums?.userRole?.map((role: any) => ({
      value: role,
      label: t(`enums.userRole.${role}`),
    })) || [];

  if (loading) {
    return <div>Loading...</div>; // or a spinner component
  }
  return (
    <React.Fragment>
      <Row>
        <Col sm={12}>
          <Card>
            <CardBody>
              <Form onSubmit={handleSubmit(onSubmit)}>
                <Row className="g-3">
                  <Col xl={5}>
                    <Form.Label className="required">
                      {t("user.firstName")}
                    </Form.Label>
                    <Controller
                      name="firstName"
                      control={control}
                      render={({ field, fieldState }) => (
                        <>
                          <Form.Control
                            type="text"
                            isInvalid={!!fieldState.error}
                            placeholder={t("user.enterFirstName")}
                            {...field}
                          />
                          <Form.Control.Feedback type="invalid">
                            {t(fieldState.error?.message as string)}
                          </Form.Control.Feedback>
                        </>
                      )}
                    />
                  </Col>
                  <Col xl={5}>
                    <Form.Label>{t("user.lastName")}</Form.Label>
                    <Controller
                      name="lastName"
                      control={control}
                      render={({ field, fieldState }) => (
                        <>
                          <Form.Control
                            type="text"
                            isInvalid={!!fieldState.error}
                            placeholder={t("user.enterLastName")}
                            {...field}
                          />
                          <Form.Control.Feedback type="invalid">
                            {t(fieldState.error?.message as string)}
                          </Form.Control.Feedback>
                        </>
                      )}
                    />
                  </Col>
                  <Col xl={2}>
                    <Form.Label>{t("user.isActive")}</Form.Label>
                    <Controller
                      name="isActive"
                      control={control}
                      render={({ field }) => (
                        <Form.Check className="form-switch">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="isActiveSwitch"
                            checked={field?.value || false}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        </Form.Check>
                      )}
                    />
                    {errors.isActive && (
                      <div className="invalid-feedback">
                        {errors.isActive.message}
                      </div>
                    )}
                  </Col>
                  <Col xl={6}>
                    <Form.Label className="required">
                      {t("user.email")}
                    </Form.Label>
                    <Controller
                      name="email"
                      control={control}
                      render={({ field, fieldState }) => (
                        <>
                          <Form.Control
                            type="email"
                            isInvalid={!!fieldState.error}
                            placeholder={t("user.enterEmail")}
                            {...field}
                          />
                          <Form.Control.Feedback type="invalid">
                            {t(fieldState.error?.message as string)}
                          </Form.Control.Feedback>
                        </>
                      )}
                    />
                  </Col>
                  <Col xl={6}>
                    <Form.Label>{t("user.mobile")}</Form.Label>
                    <Controller
                      name="mobile"
                      control={control}
                      render={({ field, fieldState }) => (
                        <>
                          <Form.Control
                            type="text"
                            isInvalid={!!fieldState.error}
                            placeholder={t("user.enterMobile")}
                            {...field}
                          />
                          <Form.Control.Feedback type="invalid">
                            {t(fieldState.error?.message as string)}
                          </Form.Control.Feedback>
                        </>
                      )}
                    />
                  </Col>
                  {!isEdit && (
                    <Col xl={6}>
                      <Form.Label className="required">
                        {t("user.password")}
                      </Form.Label>
                      <Controller
                        name="password"
                        control={control}
                        render={({ field, fieldState }) => (
                          <InputGroup hasValidation>
                            <Form.Control
                              type={showPassword ? "text" : "password"}
                              isInvalid={!!fieldState.error}
                              placeholder={t("user.enterPassword")}
                              {...field}
                            />
                            <Button
                              variant="outline-secondary"
                              type="button"
                              tabIndex={-1}
                              onClick={() => setShowPassword((prev) => !prev)}
                            >
                              <i
                                className={`ti ${showPassword ? "ti-eye-off" : "ti-eye"}`}
                              ></i>
                            </Button>
                            <Form.Control.Feedback type="invalid">
                              {t(fieldState.error?.message as string)}
                            </Form.Control.Feedback>
                          </InputGroup>
                        )}
                      />
                    </Col>
                  )}
                  <Col xl={6}>
                    <Form.Label>{t("user.roles")}</Form.Label>
                    <Controller
                      name="roles"
                      control={control}
                      render={({ field }) => (
                        <Select
                          isMulti
                          classNamePrefix="react-select"
                          className={`react-select-container ${errors.roles ? "is-invalid" : ""}`}
                          options={roleOptions}
                          value={roleOptions.filter((option: any) =>
                            field.value?.includes(option.value),
                          )}
                          onChange={(selected) =>
                            field.onChange(
                              selected.map((option) => option.value),
                            )
                          }
                          placeholder={t("user.roles")}
                        />
                      )}
                    />
                    {errors.roles && (
                      <div className="invalid-feedback">
                        {errors.roles.message}
                      </div>
                    )}
                  </Col>
                  <Col sm={12}>
                    <Form.Label className="form-label">
                      {t("user.image")}
                    </Form.Label>
                    <FormDropzone
                      onFileUpload={handleFileUpload}
                      multiple={false}
                      existingFiles={existingFiles}
                      accept="image/*"
                    />
                  </Col>
                  <Col md={12}>
                    <Form.Group className="mb-3" controlId="signature">
                      <Form.Label>{t("user.signature")}</Form.Label>
                      <Controller
                        name="signature"
                        control={control}
                        render={({ field: { onChange } }) => (
                          <>
                            <SignaturePad
                              onChange={onChange}
                              defaultValue={signatureImage?.path ?? undefined}
                            />
                            <Form.Control.Feedback type="invalid">
                              {t(errors?.signature?.message as string)}
                            </Form.Control.Feedback>
                          </>
                        )}
                      />
                    </Form.Group>
                  </Col>
                  <Col sm={12} className="d-flex gap-2">
                    <Button type="submit" variant="primary">
                      {t(`user.${isEdit ? "update" : "create"}`)}
                    </Button>
                    <Button type="button" variant="secondary" onClick={() => guardedNavigate("/users")}>
                      {t("button.cancel")}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
      {unsavedChangesModal}
    </React.Fragment>
  );
};

export default UserForm;
